import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Info,
  Plus,
  X
} from 'lucide-react';
import {
  PAYMENT_TYPE_REGISTRY,
  findCanonicalPaymentType,
  fuzzyMatchPaymentType
} from '../../utils/paymentTypeMasterRegistry';

interface PaymentTypeMapperProps {
  uniquePaymentTypes: string[];
  currentMappings: Record<string, string>;
  onMappingsChange: (mappings: Record<string, string>) => void;
}

interface MappingRow {
  key: string;
  originalValue: string;
  canonicalMatch?: string;
  confidence: number;
  status: 'matched' | 'fuzzy' | 'manual' | 'unmapped';
  customMapping?: string;
}

const PaymentTypeMapper: React.FC<PaymentTypeMapperProps> = ({
  uniquePaymentTypes,
  currentMappings,
  onMappingsChange
}) => {
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
  const [autoMappingInProgress, setAutoMappingInProgress] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customPaymentType, setCustomPaymentType] = useState('');
  const [customCanonicalMapping, setCustomCanonicalMapping] = useState('');

  // Initialize mappings on mount or when payment types change
  useEffect(() => {
    const rows: MappingRow[] = uniquePaymentTypes.map(type => {
      // Check if we have a custom mapping already
      if (currentMappings[type]) {
        return {
          key: type,
          originalValue: type,
          canonicalMatch: currentMappings[type],
          confidence: 100,
          status: 'manual',
          customMapping: currentMappings[type]
        };
      }

      // Try exact match first
      const exactMatch = findCanonicalPaymentType(type);
      if (exactMatch) {
        return {
          key: type,
          originalValue: type,
          canonicalMatch: exactMatch.canonicalName,
          confidence: 100,
          status: 'matched'
        };
      }

      // Try fuzzy match
      const fuzzyMatch = fuzzyMatchPaymentType(type);
      if (fuzzyMatch.match && fuzzyMatch.confidence > 0.7) {
        return {
          key: type,
          originalValue: type,
          canonicalMatch: fuzzyMatch.match.canonicalName,
          confidence: Math.round(fuzzyMatch.confidence * 100),
          status: 'fuzzy'
        };
      }

      // No match found
      return {
        key: type,
        originalValue: type,
        confidence: 0,
        status: 'unmapped'
      };
    });

    setMappingRows(rows);
  }, [uniquePaymentTypes, currentMappings]);

  // Auto-map all unmapped types
  const autoMapAll = () => {
    setAutoMappingInProgress(true);

    const newMappings = { ...currentMappings };

    const updatedRows = mappingRows.map(row => {
      if (row.status === 'unmapped') {
        // Try to infer from keywords
        const lowerType = row.originalValue.toLowerCase();

        let inferredType = '';
        if (lowerType.includes('upfront') && lowerType.includes('commission')) {
          inferredType = 'upfront_commission';
        } else if (lowerType.includes('trail') && lowerType.includes('commission')) {
          inferredType = 'trail_commission';
        } else if (lowerType.includes('upfront') && lowerType.includes('broker') && lowerType.includes('deduction')) {
          inferredType = 'broker_upfront_deduction';
        } else if (lowerType.includes('trail') && lowerType.includes('broker') && lowerType.includes('deduction')) {
          inferredType = 'broker_trail_deduction';
        } else if (lowerType.includes('referrer') && lowerType.includes('upfront')) {
          inferredType = 'referrer_upfront_deduction';
        } else if (lowerType.includes('referrer') && lowerType.includes('trail')) {
          inferredType = 'referrer_trail_deduction';
        } else if (lowerType.includes('clawback')) {
          inferredType = 'clawback';
        } else if (lowerType.includes('arrears')) {
          inferredType = 'arrears_commission';
        } else if (lowerType.includes('discharged')) {
          inferredType = 'discharged_commission';
        }

        if (inferredType) {
          newMappings[row.originalValue] = inferredType;
          return {
            ...row,
            canonicalMatch: inferredType,
            confidence: 80,
            status: 'fuzzy' as const,
            customMapping: inferredType
          };
        }
      }

      return row;
    });

    setMappingRows(updatedRows);
    onMappingsChange(newMappings);
    setTimeout(() => setAutoMappingInProgress(false), 500);
  };

  // Handle manual mapping change
  const handleMappingChange = (originalValue: string, canonicalValue: string) => {
    const newMappings = { ...currentMappings };

    if (canonicalValue === 'unmapped') {
      delete newMappings[originalValue];
    } else {
      newMappings[originalValue] = canonicalValue;
    }

    onMappingsChange(newMappings);

    setMappingRows(prev =>
      prev.map(row =>
        row.originalValue === originalValue
          ? {
              ...row,
              canonicalMatch: canonicalValue === 'unmapped' ? undefined : canonicalValue,
              customMapping: canonicalValue === 'unmapped' ? undefined : canonicalValue,
              status: canonicalValue === 'unmapped' ? 'unmapped' : 'manual',
              confidence: canonicalValue === 'unmapped' ? 0 : 100
            }
          : row
      )
    );
  };

  // Add custom payment type mapping
  const handleAddCustomPaymentType = () => {
    if (!customPaymentType.trim() || !customCanonicalMapping) {
      return;
    }

    // Add to mappings
    const newMappings = {
      ...currentMappings,
      [customPaymentType.trim()]: customCanonicalMapping
    };
    onMappingsChange(newMappings);

    // Add to mapping rows
    const newRow: MappingRow = {
      key: customPaymentType.trim(),
      originalValue: customPaymentType.trim(),
      canonicalMatch: customCanonicalMapping,
      confidence: 100,
      status: 'manual',
      customMapping: customCanonicalMapping
    };

    setMappingRows(prev => [...prev, newRow]);

    // Reset form
    setCustomPaymentType('');
    setCustomCanonicalMapping('');
    setShowAddCustom(false);
  };

  // Remove custom payment type
  const handleRemoveCustomPaymentType = (originalValue: string) => {
    // Only allow removal of custom-added types (not from uploaded file)
    if (uniquePaymentTypes.includes(originalValue)) {
      return; // Can't remove types from uploaded file
    }

    const newMappings = { ...currentMappings };
    delete newMappings[originalValue];
    onMappingsChange(newMappings);

    setMappingRows(prev => prev.filter(row => row.originalValue !== originalValue));
  };

  // Calculate overall mapping progress
  const mappedCount = mappingRows.filter(r => r.status !== 'unmapped').length;
  const totalCount = mappingRows.length;
  const mappingProgress = totalCount > 0 ? (mappedCount / totalCount) * 100 : 0;
  const unmappedCount = mappingRows.filter(r => r.status === 'unmapped').length;

  const getStatusBadge = (status: string, confidence: number) => {
    switch (status) {
      case 'matched':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded border border-green-500/30">
            <CheckCircle className="w-3 h-3" />
            Exact Match
          </span>
        );
      case 'fuzzy':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded border border-yellow-500/30">
            <AlertTriangle className="w-3 h-3" />
            Fuzzy ({confidence}%)
          </span>
        );
      case 'manual':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded border border-blue-500/30">
            <CheckCircle className="w-3 h-3" />
            Manual
          </span>
        );
      case 'unmapped':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30">
            <HelpCircle className="w-3 h-3" />
            Unmapped
          </span>
        );
      default:
        return null;
    }
  };

  const getCategoryBadge = (category: string) => {
    if (category === 'commission') {
      return <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Income</span>;
    }
    if (category.includes('deduction')) {
      return <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">Deduction</span>;
    }
    if (category === 'clawback') {
      return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Clawback</span>;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium text-lg">Payment Type Mapping</h3>
          <p className="text-slate-400 text-sm mt-1">
            Map payment types from the file to our canonical types
          </p>
        </div>

        <div className="flex items-center gap-4">
          {unmappedCount > 0 && (
            <span className="inline-flex items-center px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded-full">
              {unmappedCount} unmapped
            </span>
          )}
          <button
            onClick={autoMapAll}
            disabled={unmappedCount === 0 || autoMappingInProgress}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${autoMappingInProgress ? 'animate-spin' : ''}`} />
            Auto-Map Remaining
          </button>
        </div>
      </div>

      <div className="bg-slate-900/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Mapping Progress</span>
          <span className="text-sm text-white font-medium">{mappedCount} / {totalCount} mapped</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              mappingProgress === 100 ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${mappingProgress}%` }}
          />
        </div>
      </div>

      {unmappedCount > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-400 font-medium">
                {unmappedCount} payment type{unmappedCount > 1 ? 's' : ''} still unmapped
              </h4>
              <p className="text-slate-400 text-sm mt-1">
                Unmapped payment types will be ignored during processing. Use Auto-Map or manually select mappings.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-900/50 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Original Value</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Status</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-sm">Canonical Mapping</th>
                <th className="px-4 py-3 text-center text-slate-400 font-medium text-sm">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {mappingRows.map(row => {
                const isCustom = !uniquePaymentTypes.includes(row.originalValue);
                return (
                  <tr key={row.key} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="text-sm text-slate-300">{row.originalValue}</code>
                        {isCustom && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">Custom</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(row.status, row.confidence)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={row.canonicalMatch || 'unmapped'}
                          onChange={(e) => handleMappingChange(row.originalValue, e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-slate-800/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="unmapped">-- Unmapped --</option>
                          {PAYMENT_TYPE_REGISTRY.map(pt => {
                            const category = getCategoryBadge(pt.category);
                            return (
                              <option key={pt.id} value={pt.canonicalName}>
                                {pt.canonicalName}
                              </option>
                            );
                          })}
                        </select>
                        {isCustom && (
                          <button
                            onClick={() => handleRemoveCustomPaymentType(row.originalValue)}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-red-400 transition-colors"
                            title="Remove custom payment type"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-16 bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              row.confidence >= 90 ? 'bg-green-500' :
                              row.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${row.confidence}%` }}
                          />
                        </div>
                        <span className="ml-2 text-xs text-slate-400">{row.confidence}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Custom Payment Type Section */}
      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-4">
        {!showAddCustom ? (
          <button
            onClick={() => setShowAddCustom(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Custom Payment Type
          </button>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-medium">Add Custom Payment Type</h4>
              <button
                onClick={() => {
                  setShowAddCustom(false);
                  setCustomPaymentType('');
                  setCustomCanonicalMapping('');
                }}
                className="p-1 hover:bg-slate-800 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-400 text-sm">
                Add payment types that might appear in future uploads but aren't in your sample file.
                Example: Your August file has only "Upfront Commissions", but July files also contain "Trail Commissions" and "Adjustment".
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Payment Type Name (as it appears in CSV)
                </label>
                <input
                  type="text"
                  value={customPaymentType}
                  onChange={(e) => setCustomPaymentType(e.target.value)}
                  placeholder='e.g., "Trail Commissions"'
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Map to Canonical Type
                </label>
                <select
                  value={customCanonicalMapping}
                  onChange={(e) => setCustomCanonicalMapping(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Select canonical type --</option>
                  {PAYMENT_TYPE_REGISTRY.map(pt => (
                    <option key={pt.id} value={pt.canonicalName}>
                      {pt.canonicalName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAddCustomPaymentType}
                disabled={!customPaymentType.trim() || !customCanonicalMapping}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Add Mapping
              </button>
              <button
                onClick={() => {
                  setShowAddCustom(false);
                  setCustomPaymentType('');
                  setCustomCanonicalMapping('');
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-blue-400 font-medium">About Payment Type Mapping</h4>
            <ul className="text-slate-400 text-sm mt-2 space-y-1">
              <li>• Exact matches are automatically detected from our payment type registry</li>
              <li>• Fuzzy matches use similarity scoring (shown as confidence %)</li>
              <li>• You can override any automatic mapping by selecting a different canonical type</li>
              <li>• Add custom payment types that might appear in future uploads (e.g., Trail, Adjustment)</li>
              <li>• Commission types contribute to gross amounts (reportingBehavior: 'gross')</li>
              <li>• Deduction/Adjustment types reduce net commission (reportingBehavior: 'adjustment')</li>
              <li>• Clawbacks and Adjustments are always treated as negative adjustments</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTypeMapper;